import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { includes, isEmpty, isString, omit, pick, pickBy } from 'lodash-es';
import { getToken } from '@/utils/localStorageUtils';
import { message } from 'antd';
import { eventBus } from '@/utils/eventBus';


const getAxiosBaseUrl = () => process.env.NODE_ENV === 'development'
  ? 'https://regional-platform-api-dev-f2we4jwslq-uc.a.run.app'
  : process.env.REACT_APP_CHINA_REGIONAL_PLATFORM_API_ADDRESS;

interface AxiosExtraRequestConfig {
    noNeedLoading?: boolean;
}

export const pickParams = [ 'url', 'method', 'params', 'data' ];

interface IAxiosInstance extends AxiosInstance {
    // eslint-disable-next-line
    get<T = any, R = T, D = any>(url: string, config?: AxiosRequestConfig<D> & AxiosExtraRequestConfig): Promise<R>;
    // eslint-disable-next-line
    post<T = any, R = T, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D> & AxiosExtraRequestConfig): Promise<R>;
}

const axiosApi: IAxiosInstance = axios.create({
  baseURL: getAxiosBaseUrl(),
  timeout: 120000
});

export const isStreamFileResp = (contentType: string): boolean => {
  const contentTypeList = [ 'application/octet-stream' ];
  return includes(contentTypeList, contentType);
};

const pickData = item => {
  const data = pick(item, pickParams);
  return pickBy(data, value => !isEmpty(value));
};

const canDeactivateLoading = config => {
  const request = pickData(config);
  if (!isEmpty(request.data) && isString(request.data)) {
    request.data = JSON.parse(request.data);
  }
};

axiosApi.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.common['Authorization'] = `Bearer ${token}`;
    }
    return omit(config, 'noNeedLoading');
  },
  error => {
    return Promise.reject(error);
  }
);

axiosApi.interceptors.response.use(
  response => {
    canDeactivateLoading(response.config);
    if (isStreamFileResp(response.headers['content-type'])) {
      return response;
    }
    return response.data;
  },
  (error: AxiosError) => {
    const { config, response } = error;
    if (response.status >= 500) {
      message.error('系统异常，请联系管理员！');
    }
    canDeactivateLoading(config);
    if (response.status === 401) {
      eventBus.emit('unauthorized');
    }
    return Promise.reject(error);
  }
);

export default axiosApi;
